import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { createLabCase, getLabCases, getLabSuspectWorkbench, saveLabSuspectFeedback } from '../api/smartiLabApi';
import LabCard from '../components/LabCard';
import LabEmptyState from '../components/LabEmptyState';
import LabMetric from '../components/LabMetric';
import SmartiLabBadge from '../components/SmartiLabBadge';

const feedbackOptions = [
  { value: 'confirmed_root_cause', label: 'Confirmed root cause' },
  { value: 'possible', label: 'Possible' },
  { value: 'false_positive', label: 'False positive' },
  { value: 'needs_more_evidence', label: 'Needs more evidence' },
];

export default function SuspectWorkbenchPage() {
  const { candidateId } = useParams();
  const [detail, setDetail] = useState(null);
  const [cases, setCases] = useState([]);
  const [feedback, setFeedback] = useState({ feedbackLabel: 'needs_more_evidence', feedbackNote: '', caseId: '', confidenceByHuman: '0.8' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    const [detailResult, casesResult] = await Promise.all([getLabSuspectWorkbench(candidateId), getLabCases()]);
    setDetail(detailResult);
    setCases(casesResult);
  }, [candidateId]);

  useEffect(() => {
    load().catch((loadError) => setError(loadError.message || 'Unable to load the suspect workbench.'));
  }, [load]);

  const suspect = detail?.suspect;

  const handleFeedback = async (event) => {
    event.preventDefault();
    if (!suspect) return;
    setBusy(true);
    setError('');
    try {
      await saveLabSuspectFeedback({
        candidateId,
        sessionId: suspect.global_id,
        serialNumber: suspect.serialnumber,
        stepNameNorm: suspect.stepname,
        stepName: suspect.stepname || suspect.stepnumber,
        feedbackLabel: feedback.feedbackLabel,
        feedbackNote: feedback.feedbackNote,
        caseId: feedback.caseId || null,
        confidenceByHuman: Number(feedback.confidenceByHuman || 0.8),
      });
      await load();
    } catch (saveError) {
      setError(saveError.message || 'Could not save suspect feedback.');
    } finally {
      setBusy(false);
    }
  };

  const handleOpenCase = async () => {
    if (!suspect) return;
    setBusy(true);
    setError('');
    try {
      await createLabCase({
        title: `Investigate ${suspect.stepname || suspect.stepnumber}`,
        status: 'investigating',
        priority: 'high',
        linked_entity_type: 'suspect',
        linked_entity_key: String(candidateId),
        suspected_root_cause: suspect.stepname || suspect.stepnumber,
        linked_step_name: suspect.stepname || '',
        linked_station_name: suspect.station_name_norm || '',
        linked_ate_version: suspect.ate_version_norm || '',
        linked_testspec: suspect.testspec_norm || '',
        notes: `Evidence score ${Number(suspect.root_cause_evidence_score || 0).toFixed(3)}`,
      });
      await load();
    } catch (caseError) {
      setError(caseError.message || 'Could not create a case from this suspect.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lab-page">
      <LabCard title="Evidence-first suspect page" description="This page explains why the suspect ranked highly, which evidence channels pushed it upward, what units it affects, and how a human reviewer should classify it." actions={<button className="lab-button" type="button" disabled={busy} onClick={handleOpenCase}>Open case</button>}>
        {error ? <div className="lab-error-text">{error}</div> : null}
        {!suspect ? (
          <LabEmptyState title="Suspect not found" description="Open this page from a cockpit or root-cause action so Smarti Lab knows which candidate to inspect." />
        ) : (
          <>
            <div className="lab-chip-row">
              <SmartiLabBadge variant="primary" label={`Priority ${Number(suspect.investigation_priority || 0).toFixed(2)}`} />
              <SmartiLabBadge variant="success" label={`Confidence ${Number(suspect.suspect_confidence || 0).toFixed(2)}`} />
              <SmartiLabBadge variant="warning" label={suspect.station_name_norm || 'station unknown'} />
            </div>
            <div className="lab-metric-grid">
              <LabMetric label="Step" value={suspect.stepname || suspect.stepnumber} />
              <LabMetric label="Evidence score" value={suspect.root_cause_evidence_score != null ? Number(suspect.root_cause_evidence_score).toFixed(3) : '—'} />
              <LabMetric label="Model score" value={suspect.rf_weak_root_cause_score != null ? Number(suspect.rf_weak_root_cause_score).toFixed(3) : '—'} />
              <LabMetric label="Linked source" value={suspect.linked_source_label || '—'} />
            </div>
            <div className="lab-grid two-col">
              <div className="lab-human-action-panel">
                <strong>Why this ranked high</strong>
                <p>{detail?.human?.main_sentence_en || 'Smarti Lab did not have a human-readable explanation yet, so use the evidence channels and related suspects below.'}</p>
              </div>
              <div className="lab-human-action-panel">
                <strong>Affected context</strong>
                <p>{[suspect.station_name_norm, suspect.testspec_norm, suspect.machine_identifier_norm, suspect.ate_version_norm].filter(Boolean).join(' • ') || 'Context unavailable'}</p>
              </div>
            </div>
            <div className="lab-action-row">
              <Link className="lab-inline-button" to={`/smarti-lab/compare?mode=suspect&left=${encodeURIComponent(candidateId)}`}>Compare this suspect</Link>
              {suspect.serialnumber ? <Link className="lab-inline-button" to={`/smarti-lab/units/${encodeURIComponent(suspect.serialnumber)}`}>Open affected unit</Link> : null}
            </div>
          </>
        )}
      </LabCard>

      <LabCard title="Human feedback loop" description="Replace weak heuristic labels with reviewer judgement. This feedback is stored in Smarti Lab and mirrored into the fusion evidence store when possible.">
        <form className="lab-form-grid" onSubmit={handleFeedback}>
          <select className="lab-input" value={feedback.feedbackLabel} onChange={(event) => setFeedback((current) => ({ ...current, feedbackLabel: event.target.value }))}>
            {feedbackOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
          <select className="lab-input" value={feedback.caseId} onChange={(event) => setFeedback((current) => ({ ...current, caseId: event.target.value }))}>
            <option value="">No linked case</option>
            {cases.map((row) => <option key={row.id} value={row.id}>{row.title}</option>)}
          </select>
          <input className="lab-input" value={feedback.confidenceByHuman} onChange={(event) => setFeedback((current) => ({ ...current, confidenceByHuman: event.target.value }))} placeholder="Human confidence 0-1" />
          <textarea className="lab-input" value={feedback.feedbackNote} onChange={(event) => setFeedback((current) => ({ ...current, feedbackNote: event.target.value }))} placeholder="Explain why you marked it this way" />
          <button className="lab-button" type="submit" disabled={busy || !suspect}>{busy ? 'Saving...' : 'Save feedback'}</button>
        </form>
        {(detail?.feedback || []).length ? (
          <div className="lab-case-grid">
            {detail.feedback.map((row) => (
              <article key={row.id} className="lab-compare-card">
                <strong>{row.feedback_label}</strong>
                <p>{row.feedback_note || 'No note provided'}</p>
                <p>{row.owner_username} • {row.updated_at}</p>
              </article>
            ))}
          </div>
        ) : null}
      </LabCard>
    </div>
  );
}
